import { Controller } from "@hotwired/stimulus"
import { formatElapsed } from "../lib/elapsed_label"

// Updates a human-readable elapsed time for a service card and toggles the
// late modifier on its state bar.
//
// lateAtValue is a single instant — Order#late_at, the earliest moment any
// of the order's active kitchen rounds breaches its own dish-derived
// threshold (see that method's comment for why one timestamp captures the
// same fact as "any round is late"). Comparing "now" to one instant, rather
// than re-deriving each round's own fired_at/threshold client-side, is what
// keeps this in lockstep with the server without duplicating Order#late?'s
// logic in JS.
//
// The label itself uses formatElapsed — the same tiering as Kitchen's
// timer (KitchenTicket.elapsed_label) — rather than a bespoke h:mm:ss of
// its own. _service_card.html.erb renders that same tiering server-side
// for the first paint, so this has to match exactly or the label flashes
// from one format to the other a second after load.
export default class extends Controller {
  static targets = ["elapsed", "stateBar"]
  static values  = { firedAt: String, lateAt: String }

  connect() {
    if (!this.firedAtValue) return
    this.tick()
    // Every second, because the label shows seconds. On a 30s interval the
    // seconds digits sat frozen for half a minute at a time and the card read
    // as stuck rather than counting.
    this.timer = setInterval(() => this.tick(), 1000)
  }

  disconnect() { clearInterval(this.timer) }

  tick() {
    const firedAt = new Date(this.firedAtValue)
    const diffSec = Math.max(0, Math.floor((Date.now() - firedAt.getTime()) / 1000))
    if (this.hasElapsedTarget) {
      this.elapsedTarget.textContent = formatElapsed(diffSec)
    }
    // Toggling ops-card-state--late alone (without removing whatever base
    // modifier the server rendered) is enough for late to visually replace
    // the state colour: ops.css defines --late after --progress/--ready, so
    // equal-specificity cascade order — not DOM class order — decides which
    // wins whenever both are present. Same convention as Kitchen's
    // ops-timer--late toggle (kitchen_timer_controller.js).
    const isLate = !!this.lateAtValue && Date.now() >= new Date(this.lateAtValue).getTime()
    if (this.hasStateBarTarget) {
      this.stateBarTarget.classList.toggle("ops-card-state--late", isLate)
    }
  }
}
